import React, { useState, useEffect } from 'react'
import { BillOfMaterials, BOMPart, ComponentGrade, Part } from '../types'
import { BOMService, PartsService } from '../services/api'

const BOMManagement: React.FC = () => {
  const [boms, setBoms] = useState<BillOfMaterials[]>([])
  const [parts, setParts] = useState<Part[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [editingBom, setEditingBom] = useState<BillOfMaterials | null>(null)
  const [formData, setFormData] = useState<Partial<BillOfMaterials>>({
    name: '',
    deviceSku: '',
    version: '1.0',
    parts: [],
    isActive: true
  })

  // Load BOMs and parts from API
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [bomsData, partsData] = await Promise.all([
        BOMService.getAllBOMs(),
        PartsService.getAllParts()
      ])
      setBoms(bomsData)
      setParts(partsData)
    } catch (err) {
      setError('Failed to load data. Please try again.')
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editingBom) {
        // Update existing BOM
        const updatedBom = await BOMService.updateBOM(editingBom.id, formData)
        setBoms(prev => prev.map(bom => 
          bom.id === editingBom.id ? updatedBom : bom
        ))
      } else {
        // Create new BOM  
        const newBom = await BOMService.createBOM({
          name: formData.name!,
          deviceSku: formData.deviceSku!,
          version: formData.version!,
          parts: formData.parts || [],
          isActive: formData.isActive!
        })
        setBoms(prev => [...prev, newBom])
      }
      resetForm()
    } catch (err) {
      setError('Failed to save BOM. Please try again.')
      console.error('Error saving BOM:', err)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      deviceSku: '',
      version: '1.0',
      parts: [],
      isActive: true
    })
    setShowForm(false)
    setEditingBom(null)
  }

  const handleEdit = (bom: BillOfMaterials) => {
    setEditingBom(bom)
    setFormData(bom)
    setShowForm(true)
  }

  const handleDelete = async (bomId: string) => {
    if (confirm('Are you sure you want to delete this BOM?')) {
      try {
        await BOMService.deleteBOM(bomId)
        setBoms(prev => prev.filter(bom => bom.id !== bomId))
      } catch (err) {
        setError('Failed to delete BOM. Please try again.')
        console.error('Error deleting BOM:', err)
      }
    }
  }

  // Filter parts to only show BOM-compatible ones
  const bomCompatibleParts = parts.filter(part => part.bomCompatible)

  const addPartToBom = () => {
    if (bomCompatibleParts.length === 0) {
      setError('No BOM-compatible parts available. Please add parts in Parts Management first.')
      return
    }
    
    const newPart: BOMPart = {
      partId: bomCompatibleParts[0]?.id || '',
      quantity: 1,
      minGrade: ComponentGrade.GRADE_B,
      allowedGrades: [ComponentGrade.NEW, ComponentGrade.GRADE_A, ComponentGrade.GRADE_B],
      isRequired: true
    }
    setFormData(prev => ({
      ...prev,
      parts: [...(prev.parts || []), newPart]
    }))
  }

  const updateBomPart = (index: number, updatedPart: Partial<BOMPart>) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts?.map((part, i) => i === index ? { ...part, ...updatedPart } : part) || []
    }))
  }

  const removeBomPart = (index: number) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts?.filter((_, i) => i !== index) || []
    }))
  }

  const getPartInfo = (partId: string) => {
    return parts.find(p => p.id === partId)
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        Loading BOMs...
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
        <p>{error}</p>
        <button onClick={loadData} className="btn btn-primary">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Bill of Materials Management</h2>
        <button 
          onClick={() => setShowForm(true)} 
          className="btn btn-primary"
          disabled={showForm}
        >
          Create New BOM
        </button>
      </div>

      {showForm && (
        <div className="form-section" style={{ marginBottom: '2rem' }}>
          <h3>{editingBom ? 'Edit BOM' : 'Create New BOM'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="name">BOM Name *</label>
                <input
                  type="text"
                  id="name"
                  className="form-control"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="deviceSku">Device SKU *</label>
                <input
                  type="text"
                  id="deviceSku"
                  className="form-control"
                  value={formData.deviceSku || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, deviceSku: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="version">Version *</label>
                <input
                  type="text"
                  id="version"
                  className="form-control"
                  value={formData.version || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                {' '}Active BOM
              </label>
            </div>

            <div style={{ marginTop: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4>BOM Parts ({formData.parts?.length || 0})</h4>
                <button 
                  type="button" 
                  onClick={addPartToBom} 
                  className="btn btn-secondary"
                  disabled={bomCompatibleParts.length === 0}
                  title={bomCompatibleParts.length === 0 ? 'No BOM-compatible parts available' : 'Add a part to this BOM'}
                >
                  Add Part
                </button>
              </div>

              {bomCompatibleParts.length === 0 && (
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '4px', 
                  marginBottom: '1rem',
                  color: '#856404'
                }}>
                  <strong>No BOM-compatible parts available.</strong> Please add parts marked as "BOM Compatible" in Parts Management before creating BOMs.
                </div>
              )}

              {formData.parts?.map((bomPart, index) => {
                const part = getPartInfo(bomPart.partId)
                return (
                  <div key={index} style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px', 
                    padding: '1rem', 
                    marginBottom: '1rem' 
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
                      <div className="form-group">
                        <label>Part</label>
                        <select
                          className="form-control"
                          value={bomPart.partId}
                          onChange={(e) => updateBomPart(index, { partId: e.target.value })}
                        >
                          <option value="">Select a part...</option>
                          {bomCompatibleParts.map(part => (
                            <option key={part.id} value={part.id}>
                              {part.name} ({part.sku}) - ${part.costPrice.toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Quantity</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={bomPart.quantity}
                          onChange={(e) => updateBomPart(index, { quantity: parseInt(e.target.value) })}
                        />
                      </div>

                      <div className="form-group">
                        <label>Min Grade</label>
                        <select
                          className="form-control"
                          value={bomPart.minGrade || ComponentGrade.GRADE_C}
                          onChange={(e) => updateBomPart(index, { minGrade: e.target.value as ComponentGrade })}
                        >
                          <option value={ComponentGrade.NEW}>New/Unused</option>
                          <option value={ComponentGrade.GRADE_A}>Grade A</option>
                          <option value={ComponentGrade.GRADE_B}>Grade B</option>
                          <option value={ComponentGrade.GRADE_C}>Grade C</option>
                        </select>
                      </div>

                      <button 
                        type="button" 
                        onClick={() => removeBomPart(index)} 
                        className="btn btn-danger"
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ marginTop: '1rem' }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={bomPart.isRequired}
                          onChange={(e) => updateBomPart(index, { isRequired: e.target.checked })}
                        />
                        {' '}Required Part
                      </label>
                    </div>

                    {part && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                        Type: {part.partType.replace('_', ' ').toUpperCase()} | Cost: ${part.costPrice.toFixed(2)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button type="submit" className="btn btn-primary">
                {editingBom ? 'Update BOM' : 'Create BOM'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="form-section">
        <h3>BOMs List ({boms.length} BOMs)</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Device SKU</th>
              <th>Version</th>
              <th>Parts Count</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {boms.map(bom => (
              <tr key={bom.id}>
                <td><strong>{bom.name}</strong></td>
                <td>{bom.deviceSku}</td>
                <td>v{bom.version}</td>
                <td>{bom.parts.length} parts</td>
                <td>
                  <span className={`status-badge ${bom.isActive ? 'status-grade-a' : 'status-scrap'}`}>
                    {bom.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{new Date(bom.createdDate).toLocaleDateString()}</td>
                <td>
                  <button 
                    onClick={() => handleEdit(bom)} 
                    className="btn btn-secondary"
                    style={{ marginRight: '0.5rem' }}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(bom.id)} 
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {boms.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No BOMs created yet. Click "Create New BOM" to get started.
          </div>
        )}
      </div>
    </div>
  )
}

export default BOMManagement